import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getUsers } from "../api/api";
import "./style.css";

const IMAGES = [
  { src: 'https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/1.02464a56.png', bg: '#F4845F', panel: '#F79B7F', title: 'THE EXPLORER', desc: 'Journey into seamless real-time connections.' },
  { src: 'https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/2.b977faab.png', bg: '#6BBF7A', panel: '#85CC92', title: 'THE CREATOR', desc: 'Build and customize your vibrant community.' },
  { src: 'https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/3.4df853b4.png', bg: '#E882B4', panel: '#ED9DC4', title: 'THE SOCIALITE', desc: 'Stay close to your favorite people everywhere.' },
  { src: 'https://fifth-gentle-45902158.figma.site/_components/v2/4de492f6d9cf8244ad5293233e5c6f52407d42fc/4.4457fbce.png', bg: '#6EB5FF', panel: '#8DC4FF', title: 'THE VISIONARY', desc: 'Shape the future of your digital conversations.' },
];

function Home() {
  const [user, setUser] = useState(null);
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    // Original logic: Fetch user if logged in
    if (token && userId) {
      const fetchUser = async () => {
        try {
          const response = await getUsers();
          const currentUser = response.find((u) => u._id === userId);
          setUser(currentUser);
        } catch (error) {
          console.error("Error fetching user:", error);
        }
      };
      fetchUser();
    }
  }, [token, userId]);

  useEffect(() => {
    // Preload images
    IMAGES.forEach((img) => {
      const image = new Image();
      image.src = img.src;
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = (direction) => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    setActiveIndex((prev) => {
      if (direction === 'next') return (prev + 1) % 4;
      return (prev + 3) % 4;
    });

    setTimeout(() => {
      setIsAnimating(false);
    }, 650);
  };

  const getRole = (index) => {
    if (index === activeIndex) return 'center';
    if (index === (activeIndex + 3) % 4) return 'left';
    if (index === (activeIndex + 1) % 4) return 'right';
    return 'back';
  };

  const getItemStyle = (role) => {
    const baseTransition = 'transform 650ms cubic-bezier(0.4,0,0.2,1), filter 650ms cubic-bezier(0.4,0,0.2,1), opacity 650ms cubic-bezier(0.4,0,0.2,1), left 650ms cubic-bezier(0.4,0,0.2,1), bottom 650ms cubic-bezier(0.4,0,0.2,1), height 650ms cubic-bezier(0.4,0,0.2,1)';
    const willChange = 'transform, filter, opacity, left, bottom, height';
    
    if (role === 'center') {
      return {
        transform: `translateX(-50%) scale(${isMobile ? 1.25 : 1.68})`,
        filter: 'blur(0px)',
        opacity: 1,
        zIndex: 20,
        left: '50%',
        height: isMobile ? '60%' : '92%',
        bottom: isMobile ? '22%' : '0%',
        transition: baseTransition,
        willChange
      };
    } else if (role === 'left') {
      return {
        transform: 'translateX(-50%) scale(1)',
        filter: 'blur(2px)',
        opacity: 0.85,
        zIndex: 10,
        left: isMobile ? '20%' : '30%',
        height: isMobile ? '16%' : '28%',
        bottom: isMobile ? '32%' : '12%',
        transition: baseTransition,
        willChange
      };
    } else if (role === 'right') {
      return {
        transform: 'translateX(-50%) scale(1)',
        filter: 'blur(2px)',
        opacity: 0.85,
        zIndex: 10,
        left: isMobile ? '80%' : '70%',
        height: isMobile ? '16%' : '28%',
        bottom: isMobile ? '32%' : '12%',
        transition: baseTransition,
        willChange
      };
    } else { // back
      return {
        transform: 'translateX(-50%) scale(1)',
        filter: 'blur(4px)',
        opacity: 1,
        zIndex: 5,
        left: '50%',
        height: isMobile ? '13%' : '22%',
        bottom: isMobile ? '32%' : '12%',
        transition: baseTransition,
        willChange
      };
    }
  };

  const noiseSvg = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E`;

  return (
    <div 
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif",
        backgroundColor: IMAGES[activeIndex].bg,
        transition: 'background-color 650ms cubic-bezier(0.4,0,0.2,1)',
        margin: 0,
        padding: 0
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
        {/* Grain overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            pointerEvents: 'none',
            zIndex: 50,
            opacity: 0.4,
            backgroundImage: `url("${noiseSvg}")`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat'
          }}
        />

        {/* Giant ghost text */}
        <div 
          style={{ 
            position: 'absolute',
            left: 0, right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 2,
            top: '18%' 
          }}
        >
          <h1 
            style={{
              fontFamily: "'Anton', sans-serif",
              color: 'white',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              margin: 0,
              fontSize: 'clamp(90px, 25vw, 380px)',
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '0.05em',
              opacity: 1
            }}
          >
            NEXUS
          </h1>
        </div>

        {/* Carousel */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3, pointerEvents: 'none' }}>
          {IMAGES.map((img, i) => {
            const role = getRole(i);
            const style = getItemStyle(role);
            return (
              <div 
                key={i}
                style={{ position: 'absolute', ...style, aspectRatio: '0.6 / 1' }}
              >
                <img 
                  src={img.src} 
                  alt={`Figurine ${i}`}
                  draggable="false"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain', 
                    pointerEvents: 'auto',
                    objectPosition: 'bottom center' 
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Dynamic Persona Text & Nav Buttons */}
        <div 
          style={{ 
            position: 'absolute',
            bottom: isMobile ? '24px' : '80px',
            left: isMobile ? '16px' : '96px',
            zIndex: 60,
            width: 'calc(100vw - 32px)',
            maxWidth: '450px',
            marginTop: 'auto'
          }}
        >
          {/* Persona Text (Animated like Images) */}
          <div style={{ position: 'relative', width: '100%', height: isMobile ? '140px' : '200px', marginBottom: isMobile ? '16px' : '24px' }}>
            {IMAGES.map((img, i) => {
              const role = getRole(i);
              
              let transform = 'translateX(0) scale(1)';
              let opacity = 0;
              let filter = 'blur(10px)';

              if (role === 'center') {
                transform = 'translateX(0) scale(1)';
                opacity = 1;
                filter = 'blur(0px)';
              } else if (role === 'left') {
                transform = 'translateX(-30px) scale(0.95)';
                opacity = 0;
                filter = 'blur(4px)';
              } else if (role === 'right') {
                transform = 'translateX(30px) scale(0.95)';
                opacity = 0;
                filter = 'blur(4px)';
              } else {
                transform = 'translateY(20px) scale(0.9)';
                opacity = 0;
                filter = 'blur(8px)';
              }

              return (
                <div 
                  key={i}
                  style={{ 
                    position: 'absolute', 
                    bottom: 0, left: 0, width: '100%',
                    opacity,
                    transform,
                    filter,
                    transition: 'all 650ms cubic-bezier(0.4,0,0.2,1)',
                    pointerEvents: role === 'center' ? 'auto' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end'
                  }}
                >
                  <h2 style={{ 
                    fontFamily: "'Anton', sans-serif", 
                    color: 'white', 
                    fontSize: isMobile ? '2rem' : '3.5rem', 
                    margin: '0 0 8px 0',
                    lineHeight: 1,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    textShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {img.title}
                  </h2>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: 500,
                    margin: 0,
                    lineHeight: 1.4,
                    textShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    {img.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <button 
              onClick={() => navigate('prev')}
              style={{ 
                width: isMobile ? '48px' : '64px', 
                height: isMobile ? '48px' : '64px', 
                borderRadius: '50%', 
                border: '2px solid white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: 'white', 
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'transform 150ms, background-color 150ms',
                padding: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <ArrowLeft size={26} strokeWidth={2.25} />
            </button>
            <button 
              onClick={() => navigate('next')}
              style={{ 
                width: isMobile ? '48px' : '64px', 
                height: isMobile ? '48px' : '64px', 
                borderRadius: '50%', 
                border: '2px solid white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: 'white', 
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'transform 150ms, background-color 150ms',
                padding: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <ArrowRight size={26} strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
